/**
 * Buy & Hold PDF Generator
 * Handles PDF generation specifically for Buy & Hold strategy
 */

import {
  getChartData,
  graphSection,
  appreciationSection,
  expensesSection,
  revenuesSection,
  cashFlowAccumulationCalc,
} from '../utils/pdfUtils'
import { currencyFormat, percentageFormat } from '@/utils/number'
import {
  sum,
  map,
  zip,
  last,
  compact,
} from '@/utils/lodash'
import { BasePDFGenerator } from './BasePDFGenerator'
import {
  buildProformaDescriptionPdfNode,
  buildProformaDisclaimerPdfNode,
} from '@/utils/propertyDescription'

export class BuyHoldPDFGenerator extends BasePDFGenerator {
  async generatePDF() {
    return this.generateTwoPageDocDefinition()
  }

  /**
   * Generate page 1 lower right section
   */
  async generatePage1LowerRight() {
    const { property } = this
    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      acquisition = {},
      assumptions = {},
    } = property

    const IRR = acquisition.analytics.buyHold.IRR
      ? (acquisition.analytics.buyHold.IRR * 100).toFixed(2)
      : 0
    const CAPRate = acquisition.analytics.buyHold.capRate
      ? (acquisition.analytics.buyHold.capRate * 100).toFixed(2)
      : 0
    const cashOnCash = acquisition.analytics.buyHold.cashOnCashReturn
      ? (
          acquisition.analytics.buyHold.cashOnCashReturn * 100
        ).toFixed(2)
      : 0
    const equity = acquisition.analytics.buyHold.equity
      ? acquisition.analytics.buyHold.equity
      : 0
    const mortgagePayment = acquisition.analytics.buyHold.mp_monthly
      ? acquisition.analytics.buyHold.mp_monthly
      : 0
    const utilities = acquisition.analytics.buyHold
      .utilitiesTypeDollar
      ? acquisition.analytics.buyHold.utilitiesTypeDollar
      : 0
    const assetManagementFeeTotal = acquisition.analytics.buyHold
      .assetManagementFeeTotal
      ? acquisition.analytics.buyHold.assetManagementFeeTotal
      : 0

    const homeInsurance = acquisition.analytics.buyHold
      .propertyInsuranceDollar
      ? acquisition.analytics.buyHold.propertyInsuranceDollar
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.buyHold.hoaTotal
      ? acquisition.analytics.buyHold.hoaTotal / 12
      : 0
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenance
      ? assumptions.maintenance
      : 0
    const propertyMgmtPercent = acquisition.analytics.buyHold
      .propertyManagementPercentage
      ? acquisition.analytics.buyHold.propertyManagementPercentage
      : 0

    const otherExpenses = assumptions.otherExpense
      ? assumptions.otherExpense
      : 0

    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const grossRentalIncomeMonthly =
      sum(rentAmount) - vacancyRateMonthly

    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      (propertyMgmtPercent / 100) * grossRentalIncomeMonthly +
      (maintenance / 100) * grossRentalIncomeMonthly +
      utilities +
      assetManagementFeeTotal / 12 +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12

    const NOINoFinanceMonthly =
      grossRentalIncomeMonthly - totalExpenseMonthly
    const totalReturnFinancingMonthly =
      NOINoFinanceMonthly - mortgagePayment
    const totalReturnNoFinancingMonthly = NOINoFinanceMonthly

    const sqftString = sum(sqft)
      ? sum(sqft).toLocaleString('en')
      : '0'

    return [
      {
        text: ['Financial ', { text: 'Returns', color: '#3399ff' }],
        fontSize: 18,
        margin: [0, 5, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
          {
            type: 'rect',
            x: 2,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#3399ff',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 192,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 190,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#344265',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 2,
            y: 92,
            w: 365,
            h: 92,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 360 },
        stack: [
          { text: 'Financing', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(
                  totalReturnFinancingMonthly,
                ),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
        ],
      },
      {
        absolutePosition: { x: 410, y: 360 },
        stack: [
          { text: 'Cash', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(
                  totalReturnNoFinancingMonthly,
                ),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 425 },
        stack: [
          {
            text: ['Buy & Hold'],
            fontSize: 16,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 446 },
        stack: [
          {
            text: [
              'Equity:   ',
              {
                text: currencyFormat.format(equity),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 462 },
        stack: [
          {
            text: [
              'Cash on Cash:   ',
              {
                text: cashOnCash + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 478 },
        stack: [
          {
            text: [
              'IRR:  ',
              {
                text: IRR + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 494 },
        stack: [
          {
            text: [
              'CAP Rate:   ',
              {
                text: CAPRate + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },

      {
        text: [
          'Property ',
          { text: 'Information', color: '#3399ff' },
        ],
        fontSize: 18,
        margin: [0, 20, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
        ],
      },
      {
        columns: [
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Beds:   ',
                  { text: sum(beds), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Baths:   ',
                  { text: sum(baths), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Sq Feet:   ',
                  {
                    text: sqftString,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 140,
          },
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Year:   ',
                  {
                    text: property.year,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Monthly Rent:   ',
                  {
                    text: currencyFormat.format(sum(rentAmount)),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 'auto',
          },
        ],
      },
      buildProformaDescriptionPdfNode(property),
    ]
  }

  /**
   * Generate page 2 top section
   */
  async generatePage2Top() {
    const { property } = this
    const { acquisition = {}, assumptions = {} } = property

    const mortgagePayment = acquisition.analytics.buyHold.mp_monthly
      ? acquisition.analytics.buyHold.mp_monthly
      : 0
    const financeAmount = acquisition.analytics.buyHold.loanAmount
      ? acquisition.analytics.buyHold.loanAmount
      : 0
    const downPaymentTotal = acquisition.analytics.buyHold
      .downPaymentTotal
      ? acquisition.analytics.buyHold.downPaymentTotal
      : 0
    const pointsAmount = acquisition.analytics.buyHold.pointsAmount
      ? acquisition.analytics.buyHold.pointsAmount
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.buyHold
      .closeCostDollar
      ? acquisition.analytics.buyHold.closeCostDollar
      : 0
    const closeCostPercent = acquisition.analytics.buyHold
      .closeCostPercent
      ? acquisition.analytics.buyHold.closeCostPercent
      : 0
    const paydownMonthly = acquisition.analytics.buyHold
      .paydownMonthly
      ? acquisition.analytics.buyHold.paydownMonthly
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const acquisitionCommissionPercent = acquisition.analytics
      .acquisitionCommissionPercent
      ? acquisition.analytics.acquisitionCommissionPercent
      : 0
    const propertyManagementDollar = acquisition.analytics.buyHold
      .propertyManagementDollar
      ? acquisition.analytics.buyHold.propertyManagementDollar
      : 0
    const utilities = acquisition.analytics.buyHold
      .utilitiesTypeDollar
      ? acquisition.analytics.buyHold.utilitiesTypeDollar
      : 0
    const utilitiesPercent = assumptions.utilities
      ? assumptions.utilities
      : 0
    const financingFeeTotal = acquisition.analytics.buyHold
      .financingFeeTotal
      ? acquisition.analytics.buyHold.financingFeeTotal
      : 0
    const assetManagementFeeTotal = acquisition.analytics.buyHold
      .assetManagementFeeTotal
      ? acquisition.analytics.buyHold.assetManagementFeeTotal
      : 0

    const homeInsurance = acquisition.analytics.buyHold
      .propertyInsuranceDollar
      ? acquisition.analytics.buyHold.propertyInsuranceDollar
      : 0
    const maintenanceDollar = acquisition.analytics.buyHold
      .maintenanceDollar
      ? acquisition.analytics.buyHold.maintenanceDollar
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const otherIncome = acquisition.analysis.otherIncome
      ? acquisition.analysis.otherIncome
      : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.buyHold.hoaTotal
      ? acquisition.analytics.buyHold.hoaTotal / 12
      : 0
    const afterRepairValue = acquisition.analytics.buyHold
      .arvTotalBuyHold
      ? acquisition.analytics.buyHold.arvTotalBuyHold
      : 0
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentage
      ? acquisition.analysis.financingRepairsPercentage
      : 0
    const rehabCost = acquisition.analytics.buyHold.corTotal
      ? acquisition.analytics.buyHold.corTotal
      : 0
    const downPayment = assumptions.downPayment
      ? assumptions.downPayment
      : 0
    const rate = assumptions.rate ? assumptions.rate : 0
    const term = assumptions.term ? assumptions.term : 0
    const termType = assumptions.termType
      ? assumptions.termType
      : 'Months'
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenance
      ? assumptions.maintenance
      : 0
    const propertyMgmtPercent = acquisition.analytics.buyHold
      .propertyManagementPercentage
      ? acquisition.analytics.buyHold.propertyManagementPercentage
      : 0
    const pointPercentage = assumptions.points
      ? assumptions.points
      : 0
    const homePriceAppreciation = assumptions.homePriceAppreciation
      ? assumptions.homePriceAppreciation
      : 0
    const payDownAccelerator = assumptions.payDownAccelerator
      ? assumptions.payDownAccelerator
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const rentalHoldingPeriod = assumptions.rentalHoldingPeriod
      ? assumptions.rentalHoldingPeriod
      : 0
    const financingFee = acquisition.analytics.buyHold
      .financingFeePercent
      ? acquisition.analytics.buyHold.financingFeePercent
      : 0
    const sellerClosingCost = assumptions.sellerClosingCost
      ? assumptions.sellerClosingCost
      : 0
    const salesCommissionPercent = assumptions.salesCommission
      ? assumptions.salesCommission
      : 0
    const dispositionFee = assumptions.dispositionFee
      ? assumptions.dispositionFee
      : 0

    const otherExpenses = assumptions.otherExpense
      ? assumptions.otherExpense
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const monthlyRate = rate / 12 / 100
    const financedRenovationAmount =
      rehabCost - rehabCost * (downPayment / 100) || 0
    const renovationAmount = rehabCost || 0

    const graphTest = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: rentalHoldingPeriod * 12,
      paydownMonthly,
      //holdingPeriod,
      holdingPeriod: false,
    })
    const appreciation = appreciationSection({
      afterRepairValue,
      homePriceAppreciation,
      term: rentalHoldingPeriod * 12,
    })
    const sellPriceAppreciation =
      afterRepairValue +
      (last(appreciation) ? last(appreciation).appreciation : 0)
    const loanBalanceAtHoldingPeriod = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: rentalHoldingPeriod * 12,
      paydownMonthly,
      holdingPeriod: true,
    })
    const sellingClosingCostPercent = acquisition.analytics.buyHold
      .sellingClosingCostPercent
      ? acquisition.analytics.buyHold.sellingClosingCostPercent
      : 0
    const sellingClosingCostTotal =
      assumptions.sellerClosingCostType === '$'
        ? sellerClosingCost
        : (sellerClosingCost / 100) * sellPriceAppreciation
    const dispositionFeeTotal =
      assumptions.dispositionFeeType === '$'
        ? dispositionFee
        : (dispositionFee / 100) * sellPriceAppreciation
    const salesCommission =
      assumptions.salesCommissionType === '$'
        ? salesCommissionPercent
        : (salesCommissionPercent / 100) * sellPriceAppreciation

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal

    const saleProceeds =
      sellPriceAppreciation -
      salesCommission -
      sellingClosingCostTotal -
      loanBalanceAtHoldingPeriod -
      totalInitialInvestmentFinancing -
      dispositionFeeTotal
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })

    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const otherIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const revenueTotal1 = grossRentalIncome1 + sum(otherIncome) * 12
    const revenueTotal3 = grossRentalIncome3 + sum(otherIncome3)
    const revenueTotal5 = grossRentalIncome5 + sum(otherIncome5)
    const revenueTotal10 = grossRentalIncome10 + sum(otherIncome10)
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const NOINoFinance1 = revenueTotal1 - totalExpense1
    const NOINoFinance3 = revenueTotal3 - totalExpense3
    const NOINoFinance5 = revenueTotal5 - totalExpense5
    const NOINoFinance10 = revenueTotal10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })

    const cashFlowAccumulation = cashFlowAccumulationCalc({
      accelerator: payDownAccelerator,
      term: termType === 'Years' ? term * 12 + 1 : term + 1,
      rentAmount: rentAmount || [0],
      vacancy: vacancy
        ? map(vacancy, (vacancy) => vacancy / 100)
        : [0],
      propertyTaxes: propertyTaxes / 12,
      homeInsurance: homeInsurance / 12,
      monthlyHOA,
      propertyMgmtPercent: propertyMgmtPercent / 100,
      maintenance: maintenance / 100,
      utilities: utilities,
      annualRevenueIncrease: annualRevenueIncrease / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
      mortgagePayment,
    })
    const graphDataURL = getChartData(
      property,
      graphTest,
      totalReturnFinancing,
      appreciation,
      cashFlowAccumulation,
      totalInitialInvestmentFinancing,
    )

    return [
      {
        width: 400,
        stack: [
          {
            text: [
              'Financial ',
              { text: 'Projections', color: '#3399ff' },
            ],
            fontSize: 18,
            margin: [15, 20, 0, 0],
          },
          {
            canvas: [
              {
                type: 'line',
                x1: 15,
                y1: 5,
                x2: 150,
                y2: 5,
                lineWidth: 2,
              },
            ],
          },
          {
            image: graphDataURL,
            width: 400,
            height: 275,
            margin: [10, 20, 0, 0],
          },
        ],
      },
      [
        {
          margin: [20, 30],
          stack: [
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: [
                  [
                    {
                      text: 'Financing Assumptions',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  [
                    {
                      text: 'Loan Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(financeAmount),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Financed Rehab Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(
                        (financedRenovationAmount *
                          financingRepairsPercentage) /
                          100,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Interest Rate',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: rate + '%',
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage (yrs)',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: (termType === 'Years'
                        ? term
                        : term / 12
                      ).toFixed(1),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage Payment',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(mortgagePayment),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text:
                        'Paydown Acc. (' + payDownAccelerator + '%)',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(paydownMonthly),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                ],
              },
              layout: {
                defaultBorder: false,
              },
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Initial Investment',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  downPaymentTotal
                    ? [
                        {
                          text: 'Down Payment (' + downPayment + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            downPaymentTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  creditFromSeller
                    ? [
                        {
                          text: '-Credit From Seller',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            -1 * creditFromSeller,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  renovationAmount
                    ? [
                        {
                          text: 'Rehab Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            renovationAmount *
                              (1 - financingRepairsPercentage / 100),
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionFee
                    ? [
                        {
                          text: 'Acquisition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(acquisitionFee),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionCommissionDollar
                    ? [
                        {
                          text:
                            'Acquisition Commission (' +
                            acquisitionCommissionPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            acquisitionCommissionDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  closeCostDollar
                    ? [
                        {
                          text:
                            'Est Closing Cost (' +
                            closeCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            closeCostDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  financingFeeTotal
                    ? [
                        {
                          text:
                            'Financing Fee (' + financingFee + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            financingFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  pointsAmount
                    ? [
                        {
                          text: 'Points (' + pointPercentage + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(pointsAmount),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  customAcquisitionExpenseTotal
                    ? [
                        {
                          text: 'Custom Expenses',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            customAcquisitionExpenseTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Total Out Of Pocket',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(
                        totalInitialInvestmentFinancing,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Net Profit',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  sellPriceAppreciation
                    ? [
                        {
                          text: 'Sell Price at holding Period',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            sellPriceAppreciation,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  salesCommission
                    ? [
                        {
                          text:
                            'Sales Commission (' +
                            (assumptions.salesCommissionType === '$'
                              ? currencyFormat.format(
                                  salesCommissionPercent,
                                ) + ')'
                              : salesCommissionPercent + '%)'),
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            salesCommission,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  sellingClosingCostTotal
                    ? [
                        {
                          text:
                            'Seller Closing Cost (' +
                            sellingClosingCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            sellingClosingCostTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  dispositionFeeTotal
                    ? [
                        {
                          text: 'Disposition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            dispositionFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  totalInitialInvestmentFinancing
                    ? [
                        {
                          text: 'Total Out Of Pocket',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  loanBalanceAtHoldingPeriod
                    ? [
                        {
                          text: 'Loan Balance at Holding Period',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            loanBalanceAtHoldingPeriod,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Net Profit',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(saleProceeds),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
          ],
        },
      ],
    ]
  }

  /**
   * Generate page 2 bottom section
   */
  async generatePage2Bottom() {
    const { property } = this

    const {
      acquisition = {},
      assumptions = {},
      portfolio = {},
    } = property
    const { organization = {} } = portfolio
    const { legalDescription = '' } = organization
    const disclaimerNode = buildProformaDisclaimerPdfNode(legalDescription)

    const mortgagePayment = acquisition.analytics.buyHold.mp_monthly
      ? acquisition.analytics.buyHold.mp_monthly
      : 0
    const downPaymentTotal = acquisition.analytics.buyHold
      .downPaymentTotal
      ? acquisition.analytics.buyHold.downPaymentTotal
      : 0
    const pointsAmount = acquisition.analytics.buyHold.pointsAmount
      ? acquisition.analytics.buyHold.pointsAmount
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.buyHold
      .closeCostDollar
      ? acquisition.analytics.buyHold.closeCostDollar
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const propertyManagementDollar = acquisition.analytics.buyHold
      .propertyManagementDollar
      ? acquisition.analytics.buyHold.propertyManagementDollar
      : 0
    const utilities = acquisition.analytics.buyHold
      .utilitiesTypeDollar
      ? acquisition.analytics.buyHold.utilitiesTypeDollar
      : 0
    const utilitiesPercent = assumptions.utilities
      ? assumptions.utilities
      : 0
    const financingFeeTotal = acquisition.analytics.buyHold
      .financingFeeTotal
      ? acquisition.analytics.buyHold.financingFeeTotal
      : 0
    const assetManagementFeeTotal = acquisition.analytics.buyHold
      .assetManagementFeeTotal
      ? acquisition.analytics.buyHold.assetManagementFeeTotal
      : 0
    const monthlyReserves = acquisition.analytics.buyHold
      .monthlyReservesTypeDollar
      ? acquisition.analytics.buyHold.monthlyReservesTypeDollar
      : 0

    const homeInsurance = acquisition.analytics.buyHold
      .propertyInsuranceDollar
      ? acquisition.analytics.buyHold.propertyInsuranceDollar
      : 0
    const maintenanceDollar = acquisition.analytics.buyHold
      .maintenanceDollar
      ? acquisition.analytics.buyHold.maintenanceDollar
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const otherIncome = acquisition.analysis.otherIncome
      ? acquisition.analysis.otherIncome
      : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.buyHold.hoaTotal
      ? acquisition.analytics.buyHold.hoaTotal / 12
      : 0
    const offerPrice = acquisition.analysis.offerPrice
      ? acquisition.analysis.offerPrice
      : acquisition.analysis.listPrice
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentage
      ? acquisition.analysis.financingRepairsPercentage
      : 0
    const rehabCost = acquisition.analytics.buyHold.corTotal
      ? acquisition.analytics.buyHold.corTotal
      : 0
    const term = assumptions.term ? assumptions.term : 0
    const termType = assumptions.termType
      ? assumptions.termType
      : 'Months'
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenance
      ? assumptions.maintenance
      : 0
    const propertyMgmtPercent = acquisition.analytics.buyHold
      .propertyManagementPercentage
      ? acquisition.analytics.buyHold.propertyManagementPercentage
      : 0
    const payDownAccelerator = assumptions.payDownAccelerator
      ? assumptions.payDownAccelerator
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const assetManagementFee = assumptions.assetManagementFee
      ? assumptions.assetManagementFee
      : 0

    const otherExpenses = assumptions.otherExpense
      ? assumptions.otherExpense
      : 0

    const ownerReserves = assumptions.ownerReserves
      ? assumptions.ownerReserves
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const renovationAmount = rehabCost || 0

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal
    const totalInitialInvestmentNoFinancing =
      offerPrice +
      renovationAmount +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      pointsAmount +
      financingFeeTotal +
      ownerReserves +
      customAcquisitionExpenseTotal
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const otherIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const grossRentalIncomeMonthly =
      sum(rentAmount) - vacancyRateMonthly
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const monthlyRevenue = grossRentalIncomeMonthly + sum(otherIncome)
    const revenueTotal1 = grossRentalIncome1 + sum(otherIncome) * 12
    const revenueTotal3 = grossRentalIncome3 + sum(otherIncome3)
    const revenueTotal5 = grossRentalIncome5 + sum(otherIncome5)
    const revenueTotal10 = grossRentalIncome10 + sum(otherIncome10)
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const reserves1 = monthlyReserves * 12
    const reserves3 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 3,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const reserves5 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 5,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const reserves10 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 10,
      rentAmount: sum(rentAmount) * 12 - vacancyRate1,
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      (propertyMgmtPercent / 100) * grossRentalIncomeMonthly +
      (maintenance / 100) * grossRentalIncomeMonthly +
      utilities +
      assetManagementFeeTotal / 12 +
      monthlyReserves +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      reserves1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      reserves3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      reserves5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      reserves10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const expenseRatio1 = (totalExpense1 / revenueTotal1) * 100
    const expenseRatio3 = (totalExpense3 / revenueTotal3) * 100
    const expenseRatio5 = (totalExpense5 / revenueTotal5) * 100
    const expenseRatio10 = (totalExpense10 / revenueTotal10) * 100
    const NOINoFinanceMonthly = monthlyRevenue - totalExpenseMonthly
    const NOINoFinance1 = revenueTotal1 - totalExpense1
    const NOINoFinance3 = revenueTotal3 - totalExpense3
    const NOINoFinance5 = revenueTotal5 - totalExpense5
    const NOINoFinance10 = revenueTotal10 - totalExpense10
    const NOIFinanceMonthly = monthlyRevenue - totalExpenseMonthly
    const NOIFinance1 = revenueTotal1 - totalExpense1
    const NOIFinance3 = revenueTotal3 - totalExpense3
    const NOIFinance5 = revenueTotal5 - totalExpense5
    const NOIFinance10 = revenueTotal10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })
    const totalROIFinancing1 =
      (totalReturnFinancing1 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing3 =
      (totalReturnFinancing3 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing5 =
      (totalReturnFinancing5 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing10 =
      (totalReturnFinancing10 / totalInitialInvestmentFinancing) * 100
    const totalReturnNoFinancing1 = NOINoFinance1
    const totalReturnNoFinancing3 = NOINoFinance3
    const totalReturnNoFinancing5 = NOINoFinance5
    const totalReturnNoFinancing10 = NOINoFinance10
    const totalROINoFinancing1 =
      (totalReturnNoFinancing1 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing3 =
      (totalReturnNoFinancing3 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing5 =
      (totalReturnNoFinancing5 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing10 =
      (totalReturnNoFinancing10 / totalInitialInvestmentNoFinancing) *
      100

    return [
      {
        margin: [15, 10, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Annual Revenue Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Operating Expense Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Expense Ratio',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: expenseRatio1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            //[{text: 'Estimated Tax Bracket', alignment: 'left',fontSize: 6,}, {text: '$123.00', alignment: 'right',fontSize: 6,},{text: '0%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}]
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              { text: '', alignment: 'left', fontSize: 6 },
              {
                text: 'Monthly',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 1',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 3',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 5',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 10',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 0, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: compact([
            [
              {
                text: 'Revenues',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            sum(rentAmount)
              ? [
                  {
                    text: 'Potential Rental Income',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentAmount)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentAmount) * 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome3)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome5)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome10)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            vacancyRate1
              ? [
                  {
                    text:
                      'Vacancy Rate (' +
                      percentageFormat.format(
                        vacancyRateMonthly / sum(rentAmount),
                      ) +
                      ')',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRateMonthly),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            sum(otherIncome)
              ? [
                  {
                    text: 'Other Income',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(otherIncome)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sum(otherIncome) * 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherIncome3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherIncome5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherIncome10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Gross Rental Income',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(monthlyRevenue),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: compact([
            [
              {
                text: 'Expenses',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            propertyTaxes
              ? [
                  {
                    text: 'Estimated Annual Property Taxes',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            homeInsurance
              ? [
                  {
                    text: 'Estimated Annual Homeowners Insurance',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            monthlyHOA
              ? [
                  {
                    text: 'Estimated Annual HOA Dues',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(monthlyHOA),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(yearlyHOA),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            propertyManagement1
              ? [
                  {
                    text:
                      'Estimated Annual Prop Mgmt Fee (' +
                      propertyMgmtPercent +
                      '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (propertyMgmtPercent / 100) *
                        grossRentalIncomeMonthly,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyManagement1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyManagement3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyManagement5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyManagement10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            reserves1
              ? [
                  {
                    text: 'Estimated Annual Reserves',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(monthlyReserves),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            maintenance1
              ? [
                  {
                    text:
                      'Maintenance & Repairs (' + maintenance + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (maintenance / 100) * grossRentalIncomeMonthly,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            utilities
              ? [
                  {
                    text: 'Utilities',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            assetFee1
              ? [
                  {
                    text:
                      'Asset Management Fee (' +
                      assetManagementFee +
                      '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee1 / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            otherExpenses
              ? [
                  {
                    text: 'Other Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpenses / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpenses),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            customAnnualExpenseTotal
              ? [
                  {
                    text: 'Custom Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal / 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal3,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal5,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal10,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Total Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpenseMonthly),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },

      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns Without Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Cap Rate',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns With Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Mortgage',
                alignment: 'left',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePayment),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
            [
              {
                text: 'Cash On Cash',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      ...(disclaimerNode ? [disclaimerNode] : []),
    ]
  }

}
